<template>
    <modal name="move-photos" @before-open="beforeOpen" @before-close="beforeClose" :classes="[
        'v--modal',
        'error-modal',
        'upload-photos',
        hasBugs && 'has-bugs',
    ]" :width="730" :height="'auto'" :scrollable="true" :adaptive="true">
        <div class="modal-title">{{ $t("photos.moveAlbum") }}</div>
        <div class="modal-close" @click="close"></div>
        <div class="modal-body">
            <ul class="upload-photos-list">
                <li v-for="(photo, index) in photoArr" :key="index" :style="{
                    'background-image': photo.image ? 'url(' + photo.image + ')' : '',
                }" class="image-preview">
                    <div v-if="isVideo && !photo.image" class="upload-photos-process">
                        <span v-if="photo.ready">{{ $t("photos.videoProcessingError") }}</span>
                        <span v-else>{{ $t("photos.videoProcessing") }}</span>
                    </div>
                    <div class="delete-image" @click="deleteImage(index)" v-if="photo.image || photo.videos"></div>
                </li>
            </ul>

            <div class="form-group">
                <label for="album" class="label">{{ $t("photos.transferTo") }}</label>
                <multiselect v-model="album" :options="localAlbums" @select="checkAlbumOnSelect" track-by="id" label="title"
                    placeholder="" :searchable="false" selectLabel="" deselectLabel="" :option-height="26"
                    :allowEmpty="false" openDirection="bottom" id="album" name="album"
                    v-validate.persist="{ required: true, isPuzzle: [album, 600, 600] }">
                    <template slot="option" slot-scope="props">
                        <span :ref="'element' + props.option.id">{{
                            props.option.title
                        }}</span>
                    </template>
                </multiselect>
            </div>

            <div v-if="photoArr.length && !errors.has('image')" class="buttons-row">
                <button @click="close" type="button" class="button-secondary">
                    {{ $t("balanceIn.cancel") }}
                </button>
                <button @click="save" v-if="!loader" class="button">
                    {{ $t("albumEdit.move") }} {{ isVideo ? this.$t("photos.video") : this.$t("photos.photo") }}
                </button>
                <button v-if="loader" type="button" class="button button-loader"></button>
            </div>
        </div>
    </modal>
</template>

<script>
import { localizeValidation } from "@/mixins/localizeValidation";
import { observeListEnd } from "@/mixins/observeListEnd";
import { checkScroll } from "@/mixins/checkScroll";
import Multiselect from "@/components/Blocks/Multiselect";

export default {
    name: "MovePhotos",
    props: {
        i18n: Object,
    },
    components: {
        Multiselect,
    },
    mixins: [localizeValidation, observeListEnd, checkScroll],
    data() {
        return {
            hasBugs: false,
            loader: false,
            photoArr: [],
            album: "",
            currentAlbum: "",
            isVideo: false,
            moreIndex: 0,
            observer: null,
            albumsLoader: false,
        };
    },
    computed: {
        albums() {
            return this.$store.getters[
                this.getAlbumMethod() +
                "/fetch" +
                this.getMethod(true, "", false) +
                "Albums"
            ];
        },
        newAlbum() {
            return this.$store.getters[
                this.getAlbumMethod() +
                "/fetchNew" +
                this.getMethod(true, "", false) +
                "Album"
            ];
        },
        nextAlbums() {
            return this.$store.getters[
                this.getAlbumMethod() +
                "/fetchNext" +
                this.getMethod(true, "", false) +
                "Albums"
            ];
        },
        instancesErrors() {
            return this.$store.getters[this.getMethod() + "/errors"];
        },
        localAlbums() {
            let albs = [];
            for (let i in this.albums) {
                if (this.albums[i].id != this.currentAlbum.id) {
                    albs.push(this.albums[i]);
                }
            }
            albs.push({
                id: "new",
                title: this.$t("upload.newAlbom"),
                album_type: "new",
            });
            return albs;
        },
    },
    watch: {
        newAlbum() {
            this.setNewAlbum();
        },
    },
    methods: {
        getMethod(capitalize = false, method = "", isPlural = true) {
            let str = this.isVideo ? "video" : "photo";
            if (capitalize) str = str.charAt(0).toUpperCase() + str.slice(1);
            if (isPlural) str += "s";
            return str + method;
        },
        getAlbumMethod(method = "") {
            return (this.isVideo ? "videoAlbums" : "albums") + method;
        },
        beforeOpen(event) {
            this.checkScroll();
            this.localizeValidation();
            this.isVideo = event.params.isVideo || false;
            this.photoArr = event.params.instances;
            this.currentAlbum = this.photoArr[0].album;
            this.setObserver();
            this.getAlbums();

            let self = this;

            const isPuzzleRule = {
                getMessage(field, [album, width, height], data) {
                    return (
                        (data && data.message) ||
                        `${this.$t("upload.createaPuzzle")} ${width}*${height} ${this.$t("upload.pixels")}`
                    );
                },
                validate(value, [album, width, height]) {
                    if (album.album_type == 2 && self.photoArr.length) {
                        const validateImage = (file, width, height) => {
                            return new Promise((resolve) => {
                                const image = new Image();
                                image.onerror = () => resolve({ valid: false });
                                image.onload = () =>
                                    resolve({
                                        valid:
                                            image.width >= Number(width) &&
                                            image.height >= Number(height),
                                    });

                                image.src = file.image;
                            });
                        };
                        return Promise.all(
                            self.photoArr.map((file) => validateImage(file, width, height))
                        );
                    } else {
                        return { valid: true };
                    }
                },
            };

            this.$validator.extend("isPuzzle", isPuzzleRule);
        },
        getAlbums() {
            this.$store.dispatch(this.getAlbumMethod("/getMy")).then(() => {
                //console.log(this.albums);
                this.setObserver();
            });
        },
        deleteImage(index) {
            this.photoArr.splice(index, 1);
            if (this.photoArr.length == 0) {
                this.$modal.hide("move-photos");
            }
        },
        checkAlbumOnSelect(selectedOption, id) {
            if (selectedOption.album_type == "new") {
                this.$modal.show("create-album", { isVideo: this.isVideo });
                setTimeout(() => {
                    this.album = "";
                }, 100);
            }

            if (selectedOption.album_type == 2 && this.photoArr.length) {
                this.checkErrors();
            }
        },
        checkErrors() {
            let self = this;
            setTimeout(function () {
                self.$validator.validateAll().then((valid) => {
                    if (valid) {
                        if (
                            self.album &&
                            self.album.album_type == 2 &&
                            self.photoArr.length
                        ) {
                            self.$modal.show("crop-puzzle", {
                                id: self.photoArr[0].id,
                                image: self.photoArr[0].image,
                                album: self.album.id,
                                current: self.currentAlbum.id,
                            });
                            self.$modal.hide("move-photos");
                        }
                    } else {
                        self.$modal.show("dialog", {
                            title: self.$t("mixins.warning"),
                            text: self.errors.first(self.errors.items[0].field),
                            buttons: [
                                {
                                    title: self.$t("mixins.ok"),
                                    handler: () => {
                                        self.errors.clear();
                                        self.$validator.reset();
                                        self.album = "";
                                        self.$modal.hide("dialog");
                                    },
                                },
                            ],
                        });
                    }
                });
            }, 300);
        },
        setNewAlbum() {
            this.album = this.newAlbum;
        },
        save() {
            this.$validator.validateAll().then((valid) => {
                if (valid) {
                    this.loader = true;
                    let data = [];
                    let arr = this.photoArr;
                    for (let i in arr) {
                        data.push({
                            id: arr[i].id,
                            album: this.album.id,
                        });
                    }
                    this.$store
                        .dispatch(this.getMethod(false, "/update"), data)
                        .then(() => {
                            this.loader = false;
                            this.close();
                        })
                        .catch(() => {
                            if (typeof this.instancesErrors === "object") {
                                let text = "";
                                for (let i in this.instancesErrors) {
                                    for (let key in this.instancesErrors[i]) {
                                        text += this.instancesErrors[i][key][0];
                                        text += "<br>";
                                    }
                                }
                                this.$modal.show("dialog", {
                                    title: self.$t("mixins.warning"),
                                    text: text,
                                    buttons: [
                                        {
                                            title: self.$t("mixins.ok"),
                                        },
                                    ],
                                });
                            }
                            this.loader = false;
                        });
                }
            });
        },
        beforeClose() {
            this.checkRemoveScroll();
            this.errors.clear();
            this.$validator.reset();
            this.photoArr = [];
            this.album = "";
        },
        setObserver() {
            this.observer = new IntersectionObserver(
                (entries) => {
                    if (entries[0].isIntersecting === true) {
                        this.showMore();
                    }
                },
                { threshold: [0] }
            );

            this.updateMoreIndex();
            this.observeListEnd("element");
        },
        updateMoreIndex() {
            if (this.localAlbums[this.localAlbums.length - 2]) {
                this.moreIndex = this.localAlbums[this.localAlbums.length - 2].id;
            }
        },
        showMore() {
            if (this.nextAlbums) {
                this.$store.dispatch(this.getAlbumMethod("/getNext")).then(() => {
                    this.observeListEnd("element", false, true, true);
                });
            } else {
                this.observeListEnd("element", false, true);
            }
        },
        close() {
            this.$modal.hide("move-photos");
        },
    },
};
</script>

<style lang="scss">
@import "~@/assets/styles/components/_validation";
@import "~@/assets/styles/layout/_upload-photos";
</style>
